/**
 * AgentSessionContext — React Context that exposes a VoiceAgentController
 * + the overlay-visibility state to descendants.
 *
 * The CONTROLLER is plain TypeScript (framework-agnostic). This file is
 * the React-only adapter that:
 *   1. Holds the controller instance (App-level singleton via Provider).
 *   2. Adds overlay-visibility state (isMinimized) which is React-UI
 *      concern, not controller business logic.
 *   3. Exposes a `useAgentCommand(name, handler)` hook for app code to
 *      register command-bus handlers with auto-cleanup.
 *
 * Naming: `.tsx` extension = this file uses JSX / is React-only. Plain
 * `.ts` files in this folder are framework-agnostic.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import type { VoiceAgentController, VoiceAgentSnapshot } from './VoiceAgentController'
import { useVoiceAgent } from './useVoiceAgent'
import type { CommandHandler, CommandRegistry } from './types'

export interface AgentSessionContextValue {
  /** Live snapshot of the controller's state. Re-renders on change. */
  snapshot: VoiceAgentSnapshot
  /** Imperative handle to the controller (open/close/setMicActive/…). */
  controller: VoiceAgentController
  /** Command-bus the controller dispatches into. */
  registry: CommandRegistry
  /** Derived: any active status (connecting / ready / speaking / listening). */
  isActive: boolean
  /** Hat die Figur in DIESER Sitzung schon einmal gesprochen?
   *
   *  „Aktiv" heisst noch nicht „hoert zu": zwischen `connecting` und dem
   *  ersten Ton laeuft der Verbindungsaufbau, und ein Druck aufs Mikrofon
   *  verpufft dort. Alex am 2026-08-12: „Erst wenn er anfaengt, etwas zu
   *  sagen, soll der Push-to-Talk-Knopf aktiviert sein, weil vorher geht es
   *  ja nicht."
   *
   *  Die Sperre ist bewusst ein Riegel, kein Statusvergleich: Nach dem ersten
   *  Ton bleibt sie offen, auch wenn die Figur zwischendurch schweigt
   *  (`listening`, `ready`). Sonst waere der Knopf in jeder Sprechpause
   *  wieder tot. Zurueckgesetzt wird sie erst beim Ende der Sitzung. */
  hasSpoken: boolean
  /** Overlay-UI visibility flag — separate from session lifecycle so the
   *  user can hide the chat panel without ending the session. */
  isMinimized: boolean
  minimize: () => void
  restore: () => void
}

const AgentSessionContext = createContext<AgentSessionContextValue | null>(null)

const ACTIVE_STATUSES = new Set(['connecting', 'ready', 'speaking', 'listening'])

export interface AgentSessionProviderProps {
  controller: VoiceAgentController
  registry: CommandRegistry
  children: ReactNode
}

export function AgentSessionProvider({ controller, registry, children }: AgentSessionProviderProps) {
  const { controller: _, ...snapshot } = useVoiceAgent(controller)
  const isActive = ACTIVE_STATUSES.has(snapshot.status)

  /* Ableitung waehrend des Renderns statt in einem Effekt: Ein Effekt wuerde
   * erst NACH dem Bild laufen, der Knopf also fuer ein Bild lang falsch
   * dastehen — und er waere ein weiterer `setState` im Effekt, den der Linter
   * zu Recht anmerkt. Das ist Reacts eigenes Muster fuer abgeleiteten
   * Zustand. */
  const [hasSpoken, setHasSpoken] = useState(false)
  const sollteGesprochenHaben = snapshot.status === 'speaking' ? true : (isActive ? hasSpoken : false)
  if (sollteGesprochenHaben !== hasSpoken) setHasSpoken(sollteGesprochenHaben)

  const [isMinimized, setIsMinimized] = useState(false)
  const minimize = useCallback(() => setIsMinimized(true), [])
  const restore = useCallback(() => setIsMinimized(false), [])

  // Auto-un-minimize when the session ends — so the user doesn't end up
  // with a stale "minimized indicator" pointing at nothing.
  useEffect(() => {
    if (!isActive && isMinimized) setIsMinimized(false)
  }, [isActive, isMinimized])

  const value = useMemo<AgentSessionContextValue>(
    () => ({
      snapshot,
      controller,
      registry,
      isActive,
      hasSpoken,
      isMinimized,
      minimize,
      restore,
    }),
    [snapshot, controller, registry, isActive, hasSpoken, isMinimized, minimize, restore],
  )

  return <AgentSessionContext.Provider value={value}>{children}</AgentSessionContext.Provider>
}

/** Read the current agent-session context. Throws if used outside a Provider. */
export function useAgentSession(): AgentSessionContextValue {
  const ctx = useContext(AgentSessionContext)
  if (!ctx) {
    throw new Error('useAgentSession must be used inside <AgentSessionProvider>')
  }
  return ctx
}

/** Register a command-bus handler from any component. Auto-cleans up on
 *  unmount / handler-identity change. No-op outside a Provider. */
export function useAgentCommand<TArgs = unknown>(
  name: string,
  handler: CommandHandler<TArgs>,
): void {
  const ctx = useContext(AgentSessionContext)
  useEffect(() => {
    if (!ctx) return
    return ctx.registry.register(name, handler)
  }, [ctx, name, handler])
}
